import {
  type EstadoDeHoja,
  NOMBRE_RESULTADO,
  NOMBRE_SITUACION,
  type ResultadoDeHoja,
  type Situacion,
} from "@gestiondoc/shared";
import { createLink } from "@tanstack/react-router";
import type { ReactNode } from "react";

/**
 * Piezas chicas que se repiten en todas las pantallas: píldoras de estado, botones y enlaces con
 * pinta de botón, tarjetas, tablas, pestañas, campos de formulario, avisos y el estado vacío. Los
 * colores son los semánticos del mockup: verde = en orden, ámbar = alguien debe actuar, rojo =
 * fecha encima, gris = sin movimiento.
 */

const TONO_SITUACION: Record<Situacion, string> = {
  vigente: "bg-verde-fondo text-verde",
  en_revision: "bg-ambar-fondo text-ambar",
  esperando_aprobacion: "bg-ambar-fondo text-ambar",
  borrador: "bg-gris-fondo text-gris",
  revision_vencida: "bg-rojo-fondo text-rojo",
  retencion_por_vencer: "bg-rojo-fondo text-rojo",
  sin_vigente: "bg-gris-fondo text-gris",
  dispuesto: "bg-gris-fondo text-gris",
  retirado: "bg-gris-fondo text-gris",
};

export function PildoraSituacion({ situacion }: { situacion: Situacion }) {
  return <Pildora tono={TONO_SITUACION[situacion]}>{NOMBRE_SITUACION[situacion]}</Pildora>;
}

/**
 * Cómo quedó una hoja llena (fase 3b): lo que dijo quien la llenó al terminar. Rojo si hubo no
 * aceptables y falta la verificación en el sitio; gris si no lo dijo.
 */
export function PildoraDeHoja({
  estado,
  resultado,
}: {
  estado: EstadoDeHoja;
  resultado: ResultadoDeHoja | null;
}) {
  if (estado === "por_verificar") {
    return <Pildora tono="bg-rojo-fondo text-rojo">No aceptables · por verificar</Pildora>;
  }
  if (resultado === "no_aceptable") {
    return <Pildora tono="bg-ambar-fondo text-ambar">No aceptables · verificada</Pildora>;
  }
  if (resultado === "aceptable") {
    return <Pildora tono="bg-verde-fondo text-verde">{NOMBRE_RESULTADO.aceptable}</Pildora>;
  }
  return <Pildora tono="bg-gris-fondo text-gris">Sin indicar</Pildora>;
}

export function Pildora({
  children,
  tono,
  className = "",
}: {
  children: ReactNode;
  /** Fondo y texto, por ejemplo "bg-verde-fondo text-verde". */
  tono: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold whitespace-nowrap ${tono} ${className}`}
    >
      {children}
    </span>
  );
}

export type VarianteBoton = "primario" | "secundario" | "firma" | "peligro" | "discreto";

const CLASES_BOTON: Record<VarianteBoton, string> = {
  primario: "bg-marino text-white hover:bg-marino-profundo",
  secundario: "border border-borde bg-white text-marino hover:bg-fondo",
  firma: "bg-verde text-white hover:brightness-95",
  peligro: "border border-rojo/40 bg-white text-rojo hover:bg-rojo-fondo",
  /** Sin borde ni fondo: el "Cancelar" junto a los botones de verdad. */
  discreto: "text-texto-3 hover:text-marino",
};

/** La forma común de botones y enlaces con pinta de botón; `compacto` es el de las tablas. */
function claseDeBoton(variante: VarianteBoton, compacto: boolean, extra: string): string {
  const tamano = compacto ? "px-3 py-1.5 text-[12px]" : "px-3.5 py-2 text-[12.5px]";
  return `rounded-md font-semibold ${tamano} ${CLASES_BOTON[variante]} ${extra}`;
}

export function Boton({
  variante = "primario",
  compacto = false,
  children,
  className = "",
  ...resto
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: VarianteBoton;
  compacto?: boolean;
}) {
  return (
    <button
      type="button"
      {...resto}
      className={claseDeBoton(
        variante,
        compacto,
        `disabled:cursor-not-allowed disabled:opacity-60 ${className}`,
      )}
    >
      {children}
    </button>
  );
}

/** Un `<a href>` con pinta de botón: descargas y reportes que sirve la API. */
export function Enlace({
  variante = "secundario",
  compacto = false,
  className = "",
  ...resto
}: React.ComponentPropsWithRef<"a"> & { variante?: VarianteBoton; compacto?: boolean }) {
  return <a {...resto} className={claseDeBoton(variante, compacto, className)} />;
}

/** Un `<Link>` del router con pinta de botón; conserva `to`, `params` y `search` tipados. */
export const BotonEnlace = createLink(Enlace);

export function Tarjeta({
  titulo,
  ayuda,
  etiqueta,
  acento,
  children,
  className = "",
}: {
  titulo?: string;
  /** La frase en cursiva bajo el título de cada card del mockup. */
  ayuda?: string | undefined;
  /** "Se muestra porque el tipo es Registro" */
  etiqueta?: string;
  /** Borde izquierdo de 3 px (card condicional del mockup). */
  acento?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-tarjeta border border-borde bg-white p-5 ${
        acento ? "border-l-[3px] border-l-marino" : ""
      } ${className}`}
    >
      {(titulo || etiqueta) && (
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          {titulo && <h2 className="text-[15px] font-bold text-marino">{titulo}</h2>}
          {etiqueta && (
            <span className="rounded bg-marino-claro px-2 py-0.5 text-[10.5px] font-semibold text-marino">
              {etiqueta}
            </span>
          )}
        </div>
      )}
      {ayuda && <p className="mb-4 text-[12.5px] text-texto-3 italic">{ayuda}</p>}
      {children}
    </section>
  );
}

/** La tabla de las listas: en su propia tarjeta y con desplazamiento horizontal si no cabe. */
export function Tabla({ minimo, children }: { minimo: number; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-tarjeta border border-borde bg-white">
      <table className="w-full text-[13px]" style={{ minWidth: minimo }}>
        {children}
      </table>
    </div>
  );
}

/** La fila de títulos de una `Tabla`; las celdas van con `TH` y las del cuerpo con `TD`. */
export const CABECERA =
  "border-b border-borde text-left text-[10.5px] font-semibold tracking-[.08em] text-texto-3 uppercase";
export const TH = "px-4 py-2.5";
export const TD = "px-4 py-3";

/** Las pestañas de una pantalla con varias vistas sobre el mismo filtro. */
export function Pestanas<T extends string>({
  opciones,
  activa,
  onCambio,
  className = "",
}: {
  opciones: readonly { clave: T; nombre: string }[];
  activa: T;
  onCambio: (clave: T) => void;
  className?: string;
}) {
  return (
    <div role="tablist" className={`flex flex-wrap gap-1 border-b border-borde ${className}`}>
      {opciones.map((p) => (
        <button
          key={p.clave}
          type="button"
          role="tab"
          aria-selected={activa === p.clave}
          onClick={() => onCambio(p.clave)}
          className={`-mb-px border-b-2 px-3.5 py-2 text-[13px] font-semibold ${
            activa === p.clave
              ? "border-rojo-marca text-marino"
              : "border-transparent text-texto-3 hover:text-marino"
          }`}
        >
          {p.nombre}
        </button>
      ))}
    </div>
  );
}

/**
 * Fase 3: cómo entra el contenido de una revisión. Redactar es el camino nuevo; subir archivo
 * sigue siendo válido para siempre (el plan lo dice: si el editor se atrasa nadie se queda sin
 * sistema).
 */
export function SelectorDeContenido({
  redactar,
  onCambio,
}: {
  redactar: boolean;
  onCambio: (redactar: boolean) => void;
}) {
  const opcion = (activa: boolean) =>
    `flex-1 rounded-md border px-3 py-2 text-left text-[12.5px] ${
      activa
        ? "border-marino bg-marino-claro font-semibold text-marino"
        : "border-borde bg-white text-texto-2 hover:bg-fondo"
    }`;
  return (
    <fieldset className="m-0 mb-4 flex gap-2 border-0 p-0">
      <legend className="sr-only">Cómo entra el contenido</legend>
      <button
        type="button"
        aria-pressed={redactar}
        className={opcion(redactar)}
        onClick={() => onCambio(true)}
      >
        Redactar en el editor
        <span className="block text-[11px] font-normal text-texto-3">Plantilla de SMG</span>
      </button>
      <button
        type="button"
        aria-pressed={!redactar}
        className={opcion(!redactar)}
        onClick={() => onCambio(false)}
      >
        Subir archivo
        <span className="block text-[11px] font-normal text-texto-3">Word, Excel o PDF</span>
      </button>
    </fieldset>
  );
}

export function Campo({
  etiqueta,
  ayuda,
  error,
  className = "",
  children,
}: {
  etiqueta: string;
  ayuda?: string | undefined;
  error?: string | undefined;
  className?: string;
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: el control llega como children, dentro del label
    <label className={`block ${className}`}>
      <span className="mb-1 block text-[11.5px] font-semibold text-texto-2">{etiqueta}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-[11.5px] text-rojo">{error}</span>
      ) : (
        ayuda && <span className="mt-1 block text-[11.5px] text-texto-3">{ayuda}</span>
      )}
    </label>
  );
}

export const CLASE_ENTRADA =
  "w-full rounded-md border border-borde bg-fondo px-3 py-2 text-[13px] outline-none focus:border-marino disabled:opacity-70";

export function EstadoVacio({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-tarjeta border border-borde bg-white p-8 text-center text-texto-3">
      {children}
    </div>
  );
}

export function Aviso({
  tono = "rojo",
  className = "",
  children,
}: {
  tono?: "rojo" | "ambar";
  className?: string;
  children: ReactNode;
}) {
  const clase = tono === "rojo" ? "bg-rojo-fondo text-rojo" : "bg-ambar-fondo text-ambar";
  return (
    <div role="alert" className={`rounded-md px-3 py-2 text-[12.5px] ${clase} ${className}`}>
      {children}
    </div>
  );
}

/** Fila "Etiqueta · Valor" de los metadatos de cabecera (pantalla 03). */
export function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold tracking-[.08em] text-texto-3 uppercase">
        {etiqueta}
      </div>
      <div className="text-[13px] text-texto">{children}</div>
    </div>
  );
}
